/**
 * @namespace source.items
 */
import { ItemKind } from '../../constants/ItemKind';
import { MediaType } from '../../constants/MediaType';
import { toItem } from './mappers/item';
import { toError } from './parsers/error';
const getItemIdsByKind = (items, kind) => items.filter(item => item.kind === kind).map(item => item.id);
export function initItems(settings, http) {
  function mapItemList(res, thumbnailSizes, token) {
    res.data = (res.data.files || []).map(function (data) {
      return {
        ...toItem({
          ...data,
          token
        }, settings, thumbnailSizes),
        hasMoreItemsInThisCategory: res.paging && res.paging.cursor !== ''
      };
    });
    return res;
  }
  const defaultPaging = {
    size: 50,
    cursor: null
  };
  const defaultSort = {
    order: 'date',
    direction: 'desc'
  };

  /**
   * @summary Returns items
   * @memberof source.items
   * @param {Array} itemIds
   * @param {Object} [options]
   * @returns {Promise}
   * @fulfill {Items}
   * @reject {Error}
   */
  function getRequest(itemIds, options) {
    options = options || {};
    return http.post(`${settings.apiUrl}/site/media/files/info`, {
      files: itemIds
    }, null, {
      cache: false,
      headers: options.headers || {}
    }).then(function (response) {
      return mapItemList(response, options.thumbnails);
    }).catch(toError);
  }

  /**
   * @summary Returns the list of items from the given folder
   * @memberof source.items
   * @param {string} folderId
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function list(folderId, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      page_size: paging.size,
      cursor: paging.cursor,
      parent_folder_id: folderId,
      media_type: options.mediaType
    };
    queryParams.order = sort.direction === 'desc' ? `-${sort.order}` : sort.order;
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.onlyCurrentUser) {
      queryParams.current_user_only = 'True';
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/go/site/media/files/list`, queryParams, {
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails, options.siteMediaToken);
    }).catch(toError);
  }

  /**
   * @summary Returns the list of recently uploaded items
   * @memberof source.items
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function recent(options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType
    };
    queryParams.order = sort.direction === 'desc' ? `-${sort.order}` : sort.order;
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/go/site/media/files/recent`, queryParams, {
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails, options.siteMediaToken);
    }).catch(toError);
  }

  /**
   * @summary Removes items
   * @memberof source.items
   * @param {Array} items
   * @param {Object} options
   * @returns {Array}
   */
  function remove(items, options) {
    if (options === void 0) {
      options = {};
    }
    const url = `${settings.apiUrl}/site/media/delete`;
    const postOptions = {
      headers: options.headers || {}
    };
    const siteId = items[0].siteId;
    const isEveryItemString = items.every(item => typeof item === 'string');
    const body = {
      site_id: siteId,
      files_id_list: isEveryItemString ? items : getItemIdsByKind(items, ItemKind.File),
      folders_id_list: isEveryItemString ? items : getItemIdsByKind(items, ItemKind.Folder),
      ns: settings.namespace
    };
    return http.post(url, body, null, postOptions).then(response => ({
      ...response,
      data: {},
      paging: {
        size: null,
        cursor: null
      }
    })).catch(toError);
  }

  /**
   * @summary Return list of items found by tag
   * @memberof source.items
   * @param {string} tag
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function searchByTag(tag, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      tag,
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order
    };
    if (options.parentFolderId) {
      queryParams.parent_folder_id = options.parentFolderId;
    }
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    return http.get(`${settings.apiUrl}/go/site/media/files/list`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails);
    }).catch(toError);
  }

  /**
   * @summary Return list of items found by text query
   * @memberof source.items
   * @param {string} folderId
   * @param {string} query
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function search(folderId, query, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      query,
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType,
      parent_folder_id: folderId,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order
    };
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/site/media/files/search`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails, options.siteMediaToken);
    }).catch(toError);
  }

  /**
   * @summary Copy files/folders
   * @memberof source.items
   * @param {object[]} items
   * @param {object} destination
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function copy(items, destination, options) {
    if (items === void 0) {
      items = [];
    }
    if (destination === void 0) {
      destination = {};
    }
    if (options === void 0) {
      options = {};
    }
    const url = `${settings.apiUrl}/site/media/copy`;
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const {
      namespace
    } = settings;
    const contextOptions = options.isFromG6 ? namespace ? {
      ns: namespace
    } : {} : {
      owner_id: options.userId
    };
    const data = {
      items: items.map(item => ({
        item_id: item.id,
        item_type: item.kind,
        site_id: item.siteId,
        // obsolete?
        copy_name: item.name,
        media_type: item.mediaType,
        ...contextOptions
      })),
      destination: {
        folder_id: destination.id,
        site_token: destination.siteMediaToken
      }
    };
    if (namespace) {
      data.destination.ns = namespace;
    }
    const postOptions = {
      cache: false,
      headers: options.headers || {}
    };
    return http.post(url, data, null, postOptions).then(response => ({
      ...response,
      data: {
        opId: response.data.op_id
      },
      paging: {
        size: paging.size,
        cursor: response.data.cursor
      }
    })).catch(toError);
  }

  /**
   * @summary Move files/folders
   * @memberof source.items
   * @param {object[]} items
   * @param {object} destination
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function move(items, destination, options) {
    if (items === void 0) {
      items = [];
    }
    if (destination === void 0) {
      destination = {};
    }
    if (options === void 0) {
      options = {};
    }
    const fileIds = [];
    const folderIds = [];
    items.forEach(item => {
      if (item.kind === ItemKind.File) {
        fileIds.push(item.id);
      }
      if (item.kind === ItemKind.Folder) {
        folderIds.push(item.id);
      }
    });
    const data = {
      files_id_list: fileIds,
      folders_id_list: folderIds,
      destination: {
        folder_id: destination.id
      }
    };
    if (settings.namespace) {
      data.destination.ns = settings.namespace;
    }
    const postOptions = {
      cache: false,
      headers: options.headers || {}
    };
    return http.post(`${settings.apiUrl}/site/media/move`, data, null, postOptions).catch(toError);
  }
  function searchAllSitesByTag(tag, options) {
    options = options || {};
    const sort = {
      ...defaultSort,
      ...options.sort
    };
    const paging = {
      ...defaultPaging,
      ...options.paging
    };
    const queryParams = {
      page_size: paging.size,
      cursor: paging.cursor,
      media_type: options.mediaType || MediaType.Picture,
      order: sort.direction === 'desc' ? `-${sort.order}` : sort.order
    };
    return http.get(`${settings.apiUrl}/site/media/files/tags/${tag}`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(function (response) {
      response.paging = {
        size: paging.size,
        cursor: response.data.cursor
      };
      return mapItemList(response, options.thumbnails);
    });
  }

  /**
   * @summary Create zip file
   * @memberof source.items
   * @param {object[]} items
   * @param {ListSettings} options
   * @returns {Promise}
   * @fulfill {List}
   * @reject {Error}
   */
  function createZip(items, options) {
    if (items === void 0) {
      items = [];
    }
    if (options === void 0) {
      options = {};
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // +1d

    const data = {
      package_items: items.map(item => ({
        file_url: item.fileUrl,
        title: item.name
      })),
      package_id: options.packageId || 'packageId',
      package_name: options.name || 'untitled_archive',
      expiry: options.expiry || tomorrow.toISOString(),
      media_type: 'package',
      // or "secure_package"
      items_formatting: false // auto item numbering
    };
    const postOptions = {
      cache: false,
      headers: options.headers || {}
    };
    return http.post(`${settings.apiUrl}/site/media/packager/otf/download/url`, data, null, postOptions).then(response => ({
      downloadUrl: `${settings.zipperDomain}${response.data.download_url}`
    })).catch(toError);
  }
  return {
    get: getRequest,
    list,
    recent,
    remove,
    searchByTag,
    searchAllSitesByTag,
    search,
    copy,
    move,
    createZip
  };
}